import { CheckIcon } from "lucide-react";
import React from "react";
import { useObjectRef } from "react-aria";
import {
  Collection,
  composeRenderProps,
  Header,
  type ListBoxItemProps,
  ListBoxSection,
  ListBox as RACListBox,
  ListBoxItem as RACListBoxItem,
  type ListBoxProps as RACListBoxProps,
  type SectionProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import {
  cn,
  composeTailwindRenderProps,
  focusRing,
} from "@/components/ui/utils";
import { Checkbox } from "./checkbox";

export interface ListBoxProps<T>
  extends Omit<RACListBoxProps<T>, "layout" | "orientation"> {}

export function ListBox<T extends object>(
  props: ListBoxProps<T> & { ref?: React.RefObject<HTMLDivElement | null> },
) {
  const ref = useObjectRef(props.ref);

  // Fix not auto scroll to selected item
  React.useEffect(() => {
    if (ref.current) {
      const selectedItem = ref.current.querySelector("[aria-selected=true]");
      if (selectedItem) {
        const timer = setTimeout(() => {
          selectedItem.scrollIntoView({
            block: "nearest",
            behavior: "instant",
          });
        }, 50);
        return () => {
          clearTimeout(timer);
        };
      }
    }
  }, [ref]);

  return (
    <RACListBox
      {...props}
      ref={ref}
      className={composeTailwindRenderProps(
        props.className,
        "outline-0 p-1 rounded-lg",
      )}
    />
  );
}

const listBoxItemStyles = tv({
  extend: focusRing,
  base: "relative rounded-md px-2 py-1.5",
  variants: {
    isSelected: {
      true: "bg-accent text-accent-foreground",
    },
    isDisabled: {
      true: "opacity-50 cursor-not-allowed",
    },
    isFocusVisible: {
      true: "outline-ring/70 outline outline-2 outline-offset-2",
    },
  },
});

export function ListBoxItem(props: ListBoxItemProps) {
  const textValue =
    props.textValue ||
    (typeof props.children === "string" ? props.children : undefined);

  return (
    <RACListBoxItem
      {...props}
      textValue={textValue}
      className={listBoxItemStyles}
    />
  );
}

/** Inner content wrapper for select/combobox rows: label truncation; with `ItemDetail`, pair the label in `min-w-0 flex-1 truncate`. */
const dropdownItemContentClassName = cn(
  "[&:has([slot=description])]:flex [&:has([slot=description])]:flex-col",
  "[&:has([data-slot=detail])]:flex [&:has([data-slot=detail])]:flex-row [&:has([data-slot=detail])]:items-center",
  "min-w-0 flex-1 justify-center font-normal",
  "truncate [&:has([data-slot=detail])]:overflow-visible [&:has([data-slot=detail])]:text-clip",
);

export const dropdownItemStyles = tv({
  base: [
    "relative flex w-full cursor-default select-none items-center rounded-md py-1.5 pe-2 ps-8 text-sm outline-hidden",
    "[&_[slot=description]]:text-xs [&_[slot=description]]:text-muted-foreground",
  ],
  variants: {
    isDisabled: {
      true: "opacity-50 pointer-events-none",
    },
    isFocused: {
      true: "bg-accent text-accent-foreground",
    },
  },
});

export function DropdownItem(props: ListBoxItemProps) {
  const textValue =
    props.textValue ||
    (typeof props.children === "string" ? props.children : undefined);
  return (
    <RACListBoxItem
      {...props}
      textValue={textValue}
      className={composeRenderProps(props.className, (className, renderProps) =>
        dropdownItemStyles({ ...renderProps, className }),
      )}
    >
      {composeRenderProps(props.children, (children, { isSelected }) => (
        <>
          <span className="absolute start-2 flex size-3.5 items-center justify-center">
            {isSelected && <CheckIcon className="w-4 h-4" />}
          </span>
          <span className={dropdownItemContentClassName}>{children}</span>
        </>
      ))}
    </RACListBoxItem>
  );
}

/** Right-aligned secondary text in list rows (e.g. code). Pair with a label wrapped in `min-w-0 flex-1 truncate` when the label can be long. */
export function ItemDetail({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="detail"
      className={cn(
        "ml-auto shrink-0 pl-4 text-xs text-muted-foreground font-mono tabular-nums",
        className,
      )}
      {...props}
    />
  );
}

export interface DropdownSectionProps<T> extends SectionProps<T> {
  title?: string;
  items?: T[];
}

export function DropdownSection<T extends object>(
  props: DropdownSectionProps<T>,
) {
  return (
    <ListBoxSection className="first:-mt-[5px] after:content-[''] after:block after:h-[5px]">
      <Header className="text-sm font-semibold text-muted-foreground px-4 py-1 truncate sticky -top-[5px] -mt-px -mx-1 z-10 bg-accent-muted backdrop-blur-md [&+*]:mt-1">
        {props.title}
      </Header>
      <Collection items={props.items}>{props.children}</Collection>
    </ListBoxSection>
  );
}

export function MultiSelectItem(props: ListBoxItemProps) {
  const textValue =
    props.textValue ||
    (typeof props.children === "string" ? props.children : undefined);
  return (
    <RACListBoxItem
      {...props}
      textValue={textValue}
      className={composeRenderProps(props.className, (className, renderProps) =>
        dropdownItemStyles({ ...renderProps, className }),
      )}
    >
      {composeRenderProps(props.children, (children, { isSelected }) => (
        <>
          <Checkbox
            excludeFromTabOrder
            className="pointer-events-none absolute start-2 flex size-3.5 items-center justify-center"
            isSelected={isSelected}
          />
          <span className={dropdownItemContentClassName}>{children}</span>
        </>
      ))}
    </RACListBoxItem>
  );
}
